import { useEffect, useMemo, useRef, useState } from "react";
import { useAnchored } from "../lib/anchored";
import { rowMatches } from "../lib/search";

/**
 * A `<select>` you can type into.
 *
 * Jonathan, 2026-10-08: *"Every drop-down that has a selection ... should be
 * searchable by entering letters ... if there is a drop-down of entities or
 * that kind of thing, it needs to be searchable by just starting to type. ...
 * except when there is just like a few options or settings."* So this is for
 * the long, data-driven pickers — the company file, the entity, the account,
 * the vendor — and the short fixed ones (a status, a sort order, a role) stay
 * native `<select>`s, which is both his instruction and the right call: a
 * three-option list does not want a search box in front of it.
 *
 * Same anchored popover as `Menu` (fixed, shadowed, closes on outside click and
 * Escape, never crops, never moves the page). Type to filter; ↑/↓ to move;
 * Enter to pick; Escape to close. The filter is the app's own `rowMatches`, so
 * it finds an option by its label or its hint, loosely — the same box that
 * finds `13,956.88` in a table finds a company by part of its name here.
 *
 * ⚠️ Keyed/controlled exactly like the native select it replaces: `value` is
 * the chosen option's `value` ("" for an "All …" row), and `onChange` is handed
 * that string. A caller swaps `<select>` for this and changes nothing else.
 */

export interface SearchOption {
  value: string;
  label: string;
  /** Extra text that should also match a search — an entity, a bank, a number. */
  hint?: string;
  /** Options sharing a group string are shown under one heading. */
  group?: string;
}

export default function SearchSelect({
  value,
  onChange,
  options,
  placeholder = "Choose…",
  label,
  ariaLabel,
  disabled = false,
  className,
}: {
  value: string;
  onChange: (value: string) => void;
  options: SearchOption[];
  placeholder?: string;
  /** A persistent field label shown in the trigger ("Entity", "Company"), so
   *  you can find the control by what it filters, not only by its current
   *  value. The current value stays beside it as the selected tag. */
  label?: string;
  ariaLabel?: string;
  disabled?: boolean;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { anchor, panel, style } = useAnchored<HTMLButtonElement>(
    open,
    () => setOpen(false),
    "left",
  );

  const selected = options.find((o) => o.value === value) ?? null;

  const shown = useMemo(() => {
    if (!query.trim()) return options;
    return options.filter((o) => rowMatches([o.label, o.hint, o.group], query));
  }, [options, query]);

  // Keep the highlighted row inside the filtered list, and reset to the top
  // whenever the list changes under it.
  useEffect(() => {
    setActive(0);
  }, [query, open]);

  // Focus the search box when the panel opens, so typing just works.
  useEffect(() => {
    if (open) inputRef.current?.focus();
    else setQuery("");
  }, [open]);

  function pick(option: SearchOption) {
    onChange(option.value);
    setOpen(false);
  }

  function onKeyDown(event: React.KeyboardEvent) {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActive((i) => Math.min(i + 1, shown.length - 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (event.key === "Enter") {
      event.preventDefault();
      const option = shown[active];
      if (option) pick(option);
    } else if (event.key === "Escape") {
      // ⚠️ Close only THIS control, and let nothing above hear it. The pickers
      // sit inside anchored Filters panels and inside modals, every one of
      // which closes on its own Escape — so an Escape that bubbled would shut
      // the whole panel (or discard a half-typed bill) when all the reader
      // wanted was to dismiss the type-ahead. stopImmediatePropagation on the
      // native event stops it reaching those document-level listeners.
      event.preventDefault();
      event.nativeEvent.stopImmediatePropagation();
      setOpen(false);
      anchor.current?.focus();
    }
  }

  // Group the shown options in order, keeping ungrouped ones first under no head.
  const groups = useMemo(() => {
    const out: { label: string | null; options: SearchOption[] }[] = [];
    for (const option of shown) {
      const key = option.group ?? null;
      const last = out[out.length - 1];
      if (last && last.label === key) last.options.push(option);
      else out.push({ label: key, options: [option] });
    }
    return out;
  }, [shown]);

  // A flat index for arrow-key highlighting that lines up with `shown`.
  let flat = -1;

  return (
    <div className={`select-search no-print ${className ?? ""}`}>
      <button
        ref={anchor}
        type="button"
        className="select-search-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
      >
        {/* Label + value share a baseline inside this wrapper (Jonathan,
            2026-10-25); the button still centres the wrapper and the caret
            vertically. Baseline-aligning the whole button instead would push
            the text to the top of the control. */}
        <span className="select-search-text">
          {label ? <span className="select-search-label">{label}</span> : null}
          <span className={`select-search-value ${selected ? "" : "dim"}`}>
            {selected?.label ?? placeholder}
          </span>
        </span>
        <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true" className="select-caret">
          <path d="M3 4.5 6 7.5 9 4.5" fill="none" stroke="currentColor" strokeWidth="1.4" />
        </svg>
      </button>
      {open ? (
        <div ref={panel} className="menu-pop select-pop" role="dialog" aria-label={ariaLabel} style={style}>
          <input
            ref={inputRef}
            type="search"
            className="select-search-input"
            placeholder="Type to filter…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            aria-label={ariaLabel ? `Filter ${ariaLabel}` : "Filter"}
          />
          <div className="select-options" role="listbox">
            {shown.length === 0 ? (
              <div className="select-empty">No match</div>
            ) : (
              groups.map((group, gi) => (
                <div key={group.label ?? `g${gi}`} className="select-group">
                  {group.label ? <div className="select-group-head">{group.label}</div> : null}
                  {group.options.map((option) => {
                    flat += 1;
                    const index = flat;
                    return (
                      <button
                        key={option.value || "__all__"}
                        type="button"
                        role="option"
                        aria-selected={option.value === value}
                        className={`select-option ${index === active ? "active" : ""} ${
                          option.value === value ? "chosen" : ""
                        }`}
                        onMouseEnter={() => setActive(index)}
                        onClick={() => pick(option)}
                      >
                        <span className="select-option-label">{option.label}</span>
                        {option.hint ? <span className="select-option-hint">{option.hint}</span> : null}
                      </button>
                    );
                  })}
                </div>
              ))
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
